import api from '@/lib/axios';
import {
  UserProjectVO,
  DailyBriefingVO,
  EventStatsSummaryDTO,
  QueryEventStatsSummaryRequest,
  UsageSummaryVO,
  UsageSummaryRequest,
  DailyConsumptionVO,
  UsageDailyRequest,
  AdminEntityDataFindListByPageRequest,
  AdminMetaEntityFindListRequest,
  AdminMetaEntityListItem,
  EntityDataPageResponse,
} from '@/types/dashboard';

const ANALYTICS_BASE_CANDIDATES = [
  '/lowcode-business-growthtrack/api/admin/analytics/event-stats',
  '/lowcode-business/api/admin/analytics/event-stats',
] as const;
const ADMIN_DATA_BASE = (
  import.meta.env.VITE_ADMIN_DATA_API_BASE || '/lowcode-business-growthtrack/api/admin/data'
).replace(/\/+$/, '');
const ADMIN_META_ENTITY_BASE = ADMIN_DATA_BASE.replace(/\/data$/, '/meta/entity');
const ANALYTICS_BASE_STORAGE_KEY = 'analytics-event-stats-base';

let cachedAnalyticsBase: (typeof ANALYTICS_BASE_CANDIDATES)[number] | null =
  typeof window !== 'undefined'
    ? ((window.sessionStorage.getItem(ANALYTICS_BASE_STORAGE_KEY) as
        | (typeof ANALYTICS_BASE_CANDIDATES)[number]
        | null) ?? null)
    : null;

const getAnalyticsBaseCandidates = () => {
  if (!cachedAnalyticsBase) return [...ANALYTICS_BASE_CANDIDATES];
  return [
    cachedAnalyticsBase,
    ...ANALYTICS_BASE_CANDIDATES.filter((base) => base !== cachedAnalyticsBase),
  ];
};

const rememberAnalyticsBase = (base: (typeof ANALYTICS_BASE_CANDIDATES)[number]) => {
  cachedAnalyticsBase = base;
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(ANALYTICS_BASE_STORAGE_KEY, base);
};

async function postAnalyticsWithFallback<T>(
  pathSuffix: '' | '/batch',
  params:
    | QueryEventStatsSummaryRequest
    | {
        startTime: string;
        endTime: string;
        compareStartTime?: string;
        compareEndTime?: string;
        tenantIds: number[];
      },
  headers?: Record<string, string>,
): Promise<T> {
  let lastError: unknown = null;

  for (const base of getAnalyticsBaseCandidates()) {
    try {
      const result = (await api.post(`${base}/query-summary${pathSuffix}`, params, {
        headers,
        _suppressErrorToast: true,
      })) as T;
      rememberAnalyticsBase(base);
      return result;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}

async function postAdminData<T>(
  params: AdminEntityDataFindListByPageRequest,
  headers?: Record<string, string>,
): Promise<T> {
  return (await api.post(`${ADMIN_DATA_BASE}/findListByPageByTenantIds`, params, {
    headers,
    _suppressErrorToast: true,
  })) as T;
}

async function postAdminMetaEntity<T>(
  path: string,
  params: AdminMetaEntityFindListRequest,
  headers?: Record<string, string>,
): Promise<T> {
  return (await api.post(`${ADMIN_META_ENTITY_BASE}/${path}`, params, {
    headers,
    _suppressErrorToast: true,
  })) as T;
}

/**
 * 项目模块 API
 * 封装用户项目相关接口
 */
export const projectApi = {
  /**
   * 获取我的项目列表
   * POST /lowcode-business/api/user/project/list
   */
  getProjectList: (): Promise<UserProjectVO[]> => {
    return api.post('/lowcode-business/api/user/project/list');
  },
};

/**
 * 每日简报 API
 */
export const briefingApi = {
  /**
   * 获取最新每日简报
   * POST /lowcode-business/api/user/daily-briefing/latest
   */
  getLatest: (): Promise<DailyBriefingVO | null> => {
    return api.post('/lowcode-business/api/user/daily-briefing/latest');
  },
};

/**
 * 事件统计 API
 */
export const analyticsApi = {
  /**
   * 事件统计汇总查询（支持单-多渠道，支持环比）
   * POST /lowcode-business-growthtrack/api/admin/analytics/event-stats/query-summary
   */
  queryEventStatsSummary: (
    params: QueryEventStatsSummaryRequest,
    instanceToken?: string,
  ): Promise<EventStatsSummaryDTO[]> => {
    const headers: Record<string, string> = {};
    if (instanceToken) headers['X-Instance-Token'] = instanceToken;
    return postAnalyticsWithFallback<EventStatsSummaryDTO[]>('', params, headers);
  },
  batchQueryEventStatsSummary: (params: {
    startTime: string;
    endTime: string;
    compareStartTime?: string;
    compareEndTime?: string;
    tenantIds: number[];
  }): Promise<Array<{ tenantId: number; items: EventStatsSummaryDTO[] }>> => {
    return postAnalyticsWithFallback<Array<{ tenantId: number; items: EventStatsSummaryDTO[] }>>(
      '/batch',
      params,
    );
  },
};

/**
 * 管理端实体数据 API
 */
export const adminEntityDataApi = {
  /**
   * POST /lowcode-business-growthtrack/api/admin/data/findListByPageByTenantIds
   */
  findListByPageByTenantIds: <TData extends Record<string, unknown> = Record<string, unknown>>(
    params: AdminEntityDataFindListByPageRequest,
    instanceToken?: string,
  ): Promise<EntityDataPageResponse<TData>> => {
    const headers: Record<string, string> = {};
    if (instanceToken) headers['X-Instance-Token'] = instanceToken;
    return postAdminData<EntityDataPageResponse<TData>>(params, headers);
  },
};

/**
 * 管理端实体元数据 API
 */
export const adminMetaEntityApi = {
  /**
   * POST /lowcode-business-growthtrack/api/admin/meta/entity/findListByTenantIds
   */
  findListByTenantIds: (
    params: AdminMetaEntityFindListRequest,
    instanceToken?: string,
  ): Promise<AdminMetaEntityListItem[]> => {
    const headers: Record<string, string> = {};
    if (instanceToken) headers['X-Instance-Token'] = instanceToken;
    return postAdminMetaEntity<AdminMetaEntityListItem[]>('findListByTenantIds', params, headers);
  },
};

/**
 * 积分使用概览 API
 */
export const creditsApi = {
  /**
   * 查询积分使用概览（含操作次数和环比）
   * POST /lowcode-business/api/user/point/usage/summary
   */
  getUsageSummary: (params: UsageSummaryRequest): Promise<UsageSummaryVO> => {
    return api.post('/lowcode-business/api/user/point/usage/summary', params);
  },
  /**
   * 查询每日积分消耗趋势
   * POST /lowcode-business/api/user/point/usage/daily
   */
  getUsageDaily: (params: UsageDailyRequest): Promise<DailyConsumptionVO[]> => {
    return api.post('/lowcode-business/api/user/point/usage/daily', params);
  },
};
